import { useQuery } from '@tanstack/react-query';
import { searchProducts, listProducts, DataSourceFilter } from '../api/products';
import { SearchType } from '../types/product';

interface UseProductsOptions {
  searchType: SearchType;
  query: string;
  pageSize?: number;
  source?: DataSourceFilter;
  enabled?: boolean;
  exactMatch?: boolean;
  online?: boolean;
}

export function useProducts({
  searchType,
  query,
  pageSize = 20,
  source = 'all',
  enabled = true,
  exactMatch,
  online = false,
}: UseProductsOptions) {
  // When query is empty, list all products; otherwise search
  const hasQuery = query.length > 0;

  return useQuery({
    queryKey: hasQuery
      ? ['products', 'search', searchType, query, pageSize, source, exactMatch, online]
      : ['products', 'list', pageSize, source],
    queryFn: async () => {
      if (!hasQuery) {
        return listProducts(pageSize, source);
      }
      const results = await searchProducts(searchType, query, pageSize, exactMatch, online);
      // Apply source filter client-side for search results. Treat the browse
      // default ('pim') as "no filter" so search stays broad across all local
      // sources; only an explicit provider pick narrows the results.
      if (source !== 'all' && source !== 'pim') {
        return results.filter(p => p.data_source === source);
      }
      return results;
    },
    enabled,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
}
